import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AiOutlineEdit, AiOutlineExclamationCircle } from 'react-icons/ai';

import type {
  PostWorkerModifyRequestProps,
  WorkerInfo,
} from '@/types/worker.types';

import WorkerModifyModal from '@/components/worker/WorkerModifyModal';
import Text from '@/components/common/Text';
import Button from '@/components/common/Button';

import { ROUTER } from '@/constants/constants';
import { phoneFormat } from '@/utils/convertNumberFormat';

import { Theme } from '@/styles/Theme';

import * as S from './style';
import { StyledTd } from '@/styles/common';

export interface Props {
  width?: string;
  height?: string;
  hasButtons?: boolean;
  isDetailPage?: boolean;
  isWorkerPage?: boolean;
  isEditPage?: boolean;
  workerData: WorkerInfo[];
  selectedWorkerList: number[];
  setSelectedWorkerList: React.Dispatch<React.SetStateAction<number[]>>;
}

const WorkerListTable = ({
  width = '100%',
  height = '33.875rem',
  hasButtons = true,
  isDetailPage = false,
  isWorkerPage = false,
  isEditPage = false,
  workerData,
  selectedWorkerList,
  setSelectedWorkerList,
}: Props) => {
  const [isWorkerModifyModalOpen, setIsWorkerModifyModalOpen] =
    useState<boolean>(false);
  
  const navigate = useNavigate();

  const [workerInfo, setWorkerInfo] = useState<PostWorkerModifyRequestProps>({
    id: 0,
    name: '',
    password: '',
    phone: '',
    memo: '',
    noteNumber: '',
    grade: '',
  });

  const handleModifyWorker = (info: WorkerInfo) => {
    setWorkerInfo(info);
    setIsWorkerModifyModalOpen(true);
  };

  const activeWorkerList = workerData.filter(
    (worker) => worker.isActive !== '비활성'
  );
  const activeWorkerIdList = activeWorkerList.map((worker) => worker.id);
  const activeWorkerCount = activeWorkerList.length;

  const handleCheckBox = (id: number) => {
    if (selectedWorkerList.includes(id)) {
      const filteredArr = selectedWorkerList.filter((value) => value !== id);
      setSelectedWorkerList(filteredArr);
      return;
    }
    setSelectedWorkerList((prev) => [...prev, id]);
  };

  const handleCheckBoxAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    if (!checked) {
      setSelectedWorkerList([]);
      return;
    }

    setSelectedWorkerList(activeWorkerIdList);
  };

  const handleNavigateToPayment = () => {
    navigate(`${ROUTER.PAYMENT}?section=workers`);
  };

  return (
    <S.Container width={width}>
      {isWorkerModifyModalOpen && (
        <WorkerModifyModal
          workerInfo={workerInfo}
          setIsModalOpen={setIsWorkerModifyModalOpen}
        />
      )}
      {hasButtons && (
        <S.HeaderContainer>
          <Text color={Theme.colors.G_1}>
            작업자 수 : {`${activeWorkerCount} / ${workerData.length}`}
          </Text>
          <Button height='2.125rem' onClick={handleNavigateToPayment}>
            작업자 관리
          </Button>
        </S.HeaderContainer>
      )}
      <S.TableWrapper width={width} height={height}>
        <S.Table isDetailOrEditPage={isDetailPage || isEditPage}>
          <thead>
            <tr>
              {!isDetailPage && !isWorkerPage && (
                <StyledTd width='3.125rem'>
                  <input
                    type='checkbox'
                    onChange={handleCheckBoxAll}
                    checked={
                      workerData.length > 0 &&
                      JSON.stringify(selectedWorkerList) ===
                        JSON.stringify(activeWorkerIdList)
                    }
                  />
                </StyledTd>
              )}
              <th>아이디</th>
              <th>비밀번호</th>
              {isDetailPage || isEditPage ? (
                <>
                  <th>등급구분</th>
                  <th>수첩발급번호</th>
                  <th>비고</th>
                </>
              ) : (
                <>
                  <th>연락처</th>
                  <th>등급구분</th>
                  <th>수첩발급번호</th>
                  <th>비고</th>
                </>
              )}
              {!isDetailPage && <th style={{ width: '6.25rem' }}>수정</th>}
            </tr>
          </thead>
          <tbody>
            {workerData &&
              workerData.map((worker, idx) => (
                <S.StyledTr key={idx} isActive={worker.isActive === '비활성'}>
                  {!isDetailPage && !isWorkerPage && (
                    <td>
                      <input
                        type='checkbox'
                        onChange={() => handleCheckBox(worker.id)}
                        disabled={
                          worker.isActive === '비활성' &&
                          !selectedWorkerList.includes(worker.id)
                        }
                        checked={selectedWorkerList.includes(worker.id)}
                      />
                    </td>
                  )}
                  <td>{worker.name}</td>
                  <td>{worker.password}</td>
                  {isDetailPage || isEditPage ? (
                    <>
                      <td>{worker.grade || '-'}</td>
                      <td>{worker.noteNumber || '-'}</td>
                      <td>{worker.memo || '-'}</td>
                    </>
                  ) : (
                    <>
                      <td>
                        <S.PhoneCell>
                          {worker.phone ? (
                            phoneFormat(worker.phone)
                          ) : (
                            <S.WarningWrapper>
                              <AiOutlineExclamationCircle
                                size='1.25rem'
                                color={Theme.colors.WARNING_RED}
                              />
                            </S.WarningWrapper>
                          )}
                        </S.PhoneCell>
                      </td>
                      <td>{worker.grade || '-'}</td>
                      <td>{worker.noteNumber || '-'}</td>
                      <td>{worker.memo || '-'}</td>
                    </>
                  )}
                  {!isDetailPage && (
                    <td>
                      <AiOutlineEdit
                        size='1.5rem'
                        cursor='pointer'
                        onClick={() => handleModifyWorker(worker)}
                      />
                    </td>
                  )}
                </S.StyledTr>
              ))}
          </tbody>
        </S.Table>
        {workerData && workerData.length < 1 && (
          <S.NoDataText>
            <Text fontWeight={500} fontSize='1.375rem' color={Theme.colors.G_2}>
              작업자를 등록해주세요.
            </Text>
          </S.NoDataText>
        )}
      </S.TableWrapper>
    </S.Container>
  );
};

export default WorkerListTable;
